import type { RefObject } from "react";
import type { GridViewerHandle } from "../viewer/GridViewer";
import { useI18n } from "../i18n";
import { cn } from "./cn";

export interface GridControlsProps {
  viewerRef: RefObject<GridViewerHandle | null>;
  /** WO-VRD-9 — layout orientation. Top toolbar uses "horizontal". */
  orientation?: "vertical" | "horizontal";
  /** WO-VRD-10 — "sm" = compact icon buttons to sit inside the instrument strip. */
  size?: "sm" | "md";
  /** MACRO #31 PAGE 3 (mobile) — render ONLY the fit/center control (no +/−). */
  fitOnly?: boolean;
}

/**
 * GridControls — obsidian precision zoom controls (WO#5).
 *
 * Inline SVG icons (zero new deps — respects the SEP §W/§J/§AC/§AD.63
 * dependency baselines). Every button routes through the canonical
 * camera pipeline (`clampCamera`) which enforces MAX_ZOOM = 15.
 */
export function GridControls({ viewerRef, orientation = "vertical", size = "md", fitOnly = false }: GridControlsProps) {
  const { t } = useI18n();
  const zoomIn = () => viewerRef.current?.zoomIn();
  const zoomOut = () => viewerRef.current?.zoomOut();
  const fitGrid = () => viewerRef.current?.fitGrid();

  return (
    <div
      data-testid="grid-controls"
      data-shell-variant="wo-p1"
      data-orientation={orientation}
      className={cn(
        "inline-flex",
        size === "sm" ? "gap-1.5" : "gap-2",
        orientation === "horizontal" ? "flex-row" : "flex-col",
        "select-none",
      )}
      role="group"
      aria-label={t("grid.controls.group")}
    >
      {!fitOnly && (
        <>
          <ControlBtn testid="grid-controls-zoom-in-btn" aria={t("grid.controls.zoomIn")} onClick={zoomIn} size={size}>
            <IconPlus />
          </ControlBtn>
          <ControlBtn testid="grid-controls-zoom-out-btn" aria={t("grid.controls.zoomOut")} onClick={zoomOut} size={size}>
            <IconMinus />
          </ControlBtn>
        </>
      )}
      <ControlBtn testid="grid-controls-fit-btn" aria={t("grid.controls.fit")} onClick={fitGrid} size={size}>
        <IconFit />
      </ControlBtn>
    </div>
  );
}

function ControlBtn({
  testid,
  aria,
  onClick,
  size = "md",
  children,
}: {
  testid: string;
  aria: string;
  onClick: () => void;
  size?: "sm" | "md";
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      aria-label={aria}
      title={aria}
      className={cn("chamfer-btn", size === "sm" ? "h-6 w-6" : "h-11 w-11")}
    >
      {children}
    </button>
  );
}

function IconPlus() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
      <path d="M7 2v10M2 7h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}
function IconMinus() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
      <path d="M2 7h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}
function IconFit() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
      <path
        d="M2 4V2h2M8 2h2v2M10 8v2H8M4 10H2V8"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
